import { keepPreviousData } from '@tanstack/react-query';
import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';

/**
 * Shortest term the picker will send. A single character matches thousands of rows through an
 * unindexed LIKE '%...%', which is the shape behind the inventory-search pool exhaustion incident.
 * Keep in sync with MIN_SEARCH_LENGTH in backend PlatformProductService, which enforces the rule
 * server-side.
 */
export const MIN_PRODUCT_SEARCH_LENGTH = 2;

export const PLATFORM_PRODUCT_QUERY_KEY = 'platformProduct';

const platformProductsQuery = graphql(`
  query platformProducts($filter: ProductFilterInput) {
    platformProducts(filter: $filter) {
      id
      code
      name
      category
      standardUnitsCode
      piecesPerBundle {
        value
        unit
      }
    }
  }
`);

/**
 * Platform products, narrowed server-side by code or name — backs search pickers.
 *
 * Below {@link MIN_PRODUCT_SEARCH_LENGTH} nothing is fetched at all, so opening a modal no longer
 * pulls the catalogue before a word is typed. Failed searches are not retried: for a typeahead a
 * stale option list is harmless, and re-running the expensive query is exactly what a degraded
 * backend does not need.
 *
 * @param search the active search term
 * @param options pass `enabled: false` to suspend fetching while the caller's picker is closed
 *
 * @example
 *   const { activeSearch } = useDebouncedSearchInput();
 *   const { data } = usePlatformProducts(activeSearch, { enabled: isPickerOpen });
 */
export const usePlatformProducts = (
  search: string,
  options?: { enabled?: boolean }
) => {
  const term = search.trim();
  const enabled =
    (options?.enabled ?? true) && term.length >= MIN_PRODUCT_SEARCH_LENGTH;

  return useGraphqlQuery(
    platformProductsQuery,
    ['platformProducts', term],
    { filter: { search: term } },
    {
      enabled,
      retry: false,
      // Keeps the option list mounted across keystrokes, but only while searching — after the
      // search stops (cleared input, closed list) the previous term's rows would read as results.
      placeholderData: enabled ? keepPreviousData : undefined,
    }
  );
};

/**
 * The entire platform catalogue — several thousand rows in one response. Reach for
 * {@link usePlatformProducts} with a search term instead unless the caller genuinely needs every
 * product at once.
 */
export const useAllPlatformProducts = () => {
  return useGraphqlQuery(platformProductsQuery, ['platformProducts'], {});
};

export const BUY_LIST_PRODUCTS_QUERY_KEY = 'buyListProducts';

export const useGetBuyListProducts = () => {
  const buyListProductsQuery = graphql(`
    query buyListProducts {
      buyListProducts {
        id
        code
        name
      }
    }
  `);

  return useGraphqlQuery(buyListProductsQuery, [BUY_LIST_PRODUCTS_QUERY_KEY]);
};

export const MY_BUY_LIST_PRODUCTS_QUERY_KEY = 'myBuyListProducts';

export const useGetMyBuyListProducts = () => {
  const myBuyListProductsQuery = graphql(`
    query myBuyListProducts {
      myBuyListProducts {
        id
        code
        name
      }
    }
  `);

  return useGraphqlQuery(myBuyListProductsQuery, [
    MY_BUY_LIST_PRODUCTS_QUERY_KEY,
  ]);
};

export const useGetPlatformProductById = (platformProductId: string) => {
  const platformProductQuery = graphql(`
    query platformProductById($platformProductId: ID!) {
      platformProduct(id: $platformProductId) {
        actualLengthInches
        actualThicknessInches
        actualWidthInches
        category
        code
        constructionCode
        finishCode
        harmonizedCode
        id
        lengths {
          actualLengthInches
          nominalLengthInches
          displayValue
          id
        }
        materialCode
        name
        nominalThicknessInches
        nominalWidthInches
        properties
        piecesPerBundle {
          value
          unit
        }
        platformProductVehicleCapacities {
          id
          transportVehicle {
            id
            name
            vehicleType
          }
          capacity {
            value
            unit
          }
        }
        standardUnitsCode
      }
    }
  `);

  return useGraphqlQuery(
    platformProductQuery,
    [PLATFORM_PRODUCT_QUERY_KEY, platformProductId],
    { platformProductId: platformProductId }
  );
};

const ensurePlatformProductLengthMutation = graphql(`
  mutation ensurePlatformProductLength(
    $platformProductId: ID!
    $lengthId: ID!
  ) {
    ensurePlatformProductLength(
      platformProductId: $platformProductId
      lengthId: $lengthId
    ) {
      id
      isNominal
      length {
        id
        nominalLengthInches
        actualLengthInches
      }
      platformProduct {
        nominalWidthInches
        actualWidthInches
        nominalThicknessInches
        actualThicknessInches
        actualLengthInches
        piecesPerBundle {
          value
          unit
        }
      }
    }
  }
`);

export const useEnsurePlatformProductLength = () =>
  useGraphqlMutation(ensurePlatformProductLengthMutation);
